<template>
    <div class="page payment-api-page">
        <div class="page-header">
            <h1>金流串接設定</h1>
            <p>管理系統中的付款方式設定</p>
        </div>

        <div class="content vaporwave-theme">
            <!-- 工具欄 -->
            <div class="toolbar" v-if="canView">
                <div class="filters">
                    <a-space wrap size="middle">
                        <a-input
                            v-model:value="filters.keyword"
                            placeholder="搜尋代碼或名稱"
                            size="middle"
                            class="search-input-field min-w-[300px]"
                            allow-clear
                            @press-enter="handleSearch"
                        />
                        <a-select
                            v-model:value="filters.status"
                            placeholder="狀態"
                            allow-clear
                            size="middle"
                            class="status-select min-w-[120px]"
                            :options="statusOptions"
                            @change="handleSearch"
                            @clear="handleSearch"
                        />
                    </a-space>
                </div>
                <div class="toolbar-actions">
                    <a-space>
                        <a-button
                            size="middle"
                            class="unified-search-btn"
                            @click="handleSearch"
                        >
                            查詢
                        </a-button>
                        <a-button
                            v-if="canCreate"
                            type="primary"
                            size="middle"
                            class="unified-add-btn"
                            @click="showCreateModal"
                        >
                            新增付款方式
                        </a-button>
                    </a-space>
                </div>
            </div>

            <!-- 付款方式列表表格 -->
            <DataTable
                title=""
                :columns="columns"
                :data-source="paymentMethods"
                :pagination="pagination"
                :show-index="false"
                @refresh="loadPaymentMethods"
                @change="handleTableChange"
                class="payment-methods-table"
            >
                <template #bodyCell="{ column, record }">
                    <template v-if="column.dataIndex === 'code'">
                        <span class="code-text">{{ record.code }}</span>
                    </template>

                    <template v-else-if="column.dataIndex === 'status'">
                        <a-tag :color="record.status === 1 ? 'success' : 'red'">
                            {{ record.status === 1 ? '啟用' : '停用' }}
                        </a-tag>
                    </template>

                    <template v-else-if="column.dataIndex === 'created_at'">
                        {{ formatDate(record.created_at) }}
                    </template>

                    <template v-else-if="column.dataIndex === 'updated_at'">
                        {{ formatDate(record.updated_at) }}
                    </template>

                    <template v-else-if="column.key === 'actions'">
                        <a-space class="action-buttons">
                            <a-button
                                v-if="canUpdate"
                                size="small"
                                class="action-btn-edit"
                                @click="showEditModal(record)"
                            >
                                編輯
                            </a-button>
                            <a-button
                                v-if="canDelete"
                                size="small"
                                class="action-btn-delete"
                                @click="openDeleteModal(record)"
                            >
                                刪除
                            </a-button>
                        </a-space>
                    </template>
                </template>
            </DataTable>
        </div>

        <!-- 新增/編輯付款方式 Modal -->
        <a-modal
            v-model:open="modalVisible"
            :title="isEdit ? '編輯付款方式' : '新增付款方式'"
            ok-text="確定"
            cancel-text="取消"
            :width="isNewebpayForm ? 640 : 520"
            @ok="handleSubmit"
            @cancel="resetForm"
            :confirm-loading="submitting"
            class="dark-modal"
        >
            <a-form
                ref="formRef"
                :model="formData"
                :rules="formRules"
                layout="vertical"
                class="payment-method-form"
            >
                <a-form-item label="付款方式代碼" name="code">
                    <a-input
                        v-model:value="formData.code"
                        placeholder="請輸入付款方式代碼（如：credit, linepay）"
                        :disabled="isEdit"
                    />
                    <div class="form-item-hint" v-if="!isEdit">
                        代碼必須唯一，建立後無法修改
                    </div>
                </a-form-item>

                <a-form-item label="付款方式名稱" name="name">
                    <a-input
                        v-model:value="formData.name"
                        placeholder="請輸入付款方式名稱（如：信用卡、LINE Pay）"
                    />
                </a-form-item>

                <a-form-item label="狀態" name="status">
                    <a-radio-group v-model:value="formData.status">
                        <a-radio :value="1">啟用</a-radio>
                        <a-radio :value="0">停用</a-radio>
                    </a-radio-group>
                </a-form-item>

                <template v-if="isNewebpayForm">
                    <a-divider orientation="left" class="mpg-divider">
                        藍新 MPG 通道
                    </a-divider>
                    <a-alert
                        type="info"
                        show-icon
                        class="mpg-alert"
                        message="此處開關會覆寫伺服器環境變數；未開通的通道請勿開啟，否則 MPG 可能整筆跳轉失敗。"
                    />
                    <div class="mpg-grid">
                        <div
                            v-for="ch in mpgChannelOptions"
                            :key="ch.key"
                            class="mpg-row"
                        >
                            <div class="mpg-row__text">
                                <span class="mpg-row__label">{{ ch.label }}</span>
                                <span v-if="ch.hint" class="mpg-row__hint">{{
                                    ch.hint
                                }}</span>
                            </div>
                            <a-switch
                                v-model:checked="formData.mpg_channels[ch.key]"
                                checked-children="開"
                                un-checked-children="關"
                            />
                        </div>
                    </div>
                </template>
            </a-form>
        </a-modal>

        <!-- 刪除付款方式 Modal -->
        <a-modal
            v-model:open="deleteModalVisible"
            title="刪除付款方式"
            :width="600"
            :confirm-loading="deleteLoading"
            @ok="confirmDelete"
            @cancel="deleteModalVisible = false"
            class="dark-modal"
        >
            <div v-if="deleteTarget" class="delete-modal-content">
                <!-- 付款方式資訊 -->
                <div class="delete-payment-info">
                    <h3 class="delete-section-title">付款方式資訊</h3>
                    <a-descriptions :column="2" bordered size="small">
                        <a-descriptions-item label="付款方式代碼">
                            <span class="payment-code-display">
                                {{ deleteTarget.code }}
                            </span>
                        </a-descriptions-item>
                        <a-descriptions-item label="付款方式名稱">
                            <span class="payment-name-display">
                                {{ deleteTarget.name }}
                            </span>
                        </a-descriptions-item>
                        <a-descriptions-item label="狀態">
                            <a-tag
                                :color="
                                    deleteTarget.status === 1 ? 'green' : 'red'
                                "
                            >
                                {{
                                    deleteTarget.status === 1 ? '啟用' : '停用'
                                }}
                            </a-tag>
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="deleteTarget.created_at"
                            label="建立時間"
                            :span="2"
                        >
                            {{ formatDate(deleteTarget.created_at) }}
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="deleteTarget.updated_at"
                            label="更新時間"
                            :span="2"
                        >
                            {{ formatDate(deleteTarget.updated_at) }}
                        </a-descriptions-item>
                    </a-descriptions>
                </div>

                <!-- 操作提示 -->
                <div class="delete-action-hint">
                    <a-alert
                        type="warning"
                        message="此操作無法復原，請確認後再執行"
                        show-icon
                    />
                </div>
            </div>
            <template #footer>
                <a-button @click="deleteModalVisible = false">取消</a-button>
                <a-button
                    type="primary"
                    danger
                    :loading="deleteLoading"
                    @click="confirmDelete"
                >
                    確認刪除
                </a-button>
            </template>
        </a-modal>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { message } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';
import DataTable from '@/components/common/DataTable.vue';
import {
    getPaymentMethods,
    getPaymentMethodDetail,
    createPaymentMethod,
    updatePaymentMethod,
    deletePaymentMethod
} from '@/api/payment-methods';
import { useAdminInfo } from '@/stores/useAdminInfo';
import formatDateTime from '@/utils/formatDateTime';

// 權限檢查
const adminInfo = useAdminInfo();
const canCreate = computed(() => adminInfo.hasPermissionCode(900301));
const canUpdate = computed(() => adminInfo.hasPermissionCode(900302));
const canDelete = computed(() => adminInfo.hasPermissionCode(900303));
const canView = computed(() => adminInfo.hasPermissionCode(900304));

// 響應式數據
const paymentMethods = ref([]);
const loading = ref(false);
const modalVisible = ref(false);
const isEdit = ref(false);
const submitting = ref(false);
const formRef = ref();
const deleteModalVisible = ref(false);
const deleteTarget = ref(null);
const deleteLoading = ref(false);
const originalData = ref(null);

// 篩選器
const filters = reactive({
    keyword: '',
    status: undefined
});

// 狀態選項
const statusOptions = [
    { label: '啟用', value: 1 },
    { label: '停用', value: 0 }
];

// 分頁配置
const pagination = reactive({
    current: 1,
    pageSize: 15,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `第 ${range[0]}-${range[1]} 項，共 ${total} 項`
});

// 表格欄位配置
const columns = [
    {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80,
        align: 'center'
    },
    {
        title: '付款方式代碼',
        dataIndex: 'code',
        key: 'code',
        width: 150
    },
    {
        title: '付款方式名稱',
        dataIndex: 'name',
        key: 'name',
        width: 200
    },
    {
        title: '狀態',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        align: 'center'
    },
    {
        title: '建立時間',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 180
    },
    {
        title: '更新時間',
        dataIndex: 'updated_at',
        key: 'updated_at',
        width: 180
    },
    {
        title: '操作',
        key: 'actions',
        width: 150,
        fixed: 'right',
        align: 'center'
    }
];

// 表單數據
const defaultMpgChannels = () => ({
    credit_card: true,
    webatm: false,
    vacc: false,
    cvs: false,
    barcode: false,
    apple_pay: false,
    android_pay: false,
    samsung_pay: false
});

const formData = reactive({
    id: null,
    code: '',
    name: '',
    status: 1,
    mpg_channels: defaultMpgChannels()
});

const isNewebpayForm = computed(() =>
    String(formData.code || '').toLowerCase().startsWith('newebpay')
);

const mpgChannelOptions = [
    { key: 'credit_card', label: '信用卡一次付清', hint: 'CREDIT' },
    { key: 'webatm', label: 'WebATM', hint: '網路ATM' },
    { key: 'vacc', label: 'ATM 轉帳', hint: '虛擬帳號 VACC' },
    { key: 'cvs', label: '超商代碼', hint: 'CVS' },
    { key: 'barcode', label: '超商條碼', hint: 'BARCODE' },
    {
        key: 'apple_pay',
        label: 'Apple Pay',
        hint: '商店未開通請關閉'
    },
    {
        key: 'android_pay',
        label: 'Google Pay',
        hint: '藍新參數 ANDROIDPAY；未開通請關閉'
    },
    { key: 'samsung_pay', label: 'Samsung Pay', hint: '未開通請關閉' }
];

const applyMpgChannels = (source) => {
    const base = defaultMpgChannels();
    const fromApi = source?.mpg_channels || source?.settings?.mpg_channels || {};
    Object.keys(base).forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(fromApi, key)) {
            base[key] = Boolean(fromApi[key]);
        }
    });
    return base;
};

// 表單驗證規則
const formRules = {
    code: [
        { required: true, message: '請輸入付款方式代碼', trigger: 'blur' },
        {
            max: 50,
            message: '代碼長度不能超過 50 個字符',
            trigger: 'blur'
        },
        {
            pattern: /^[a-z0-9_]+$/,
            message: '代碼只能包含小寫字母、數字和底線',
            trigger: 'blur'
        }
    ],
    name: [
        { required: true, message: '請輸入付款方式名稱', trigger: 'blur' },
        {
            max: 100,
            message: '名稱長度不能超過 100 個字符',
            trigger: 'blur'
        }
    ],
    status: [{ required: true, message: '請選擇狀態', trigger: 'change' }]
};

// 日期格式化
const formatDate = (dateString) => {
    if (!dateString) return '-';
    const formatted = formatDateTime(dateString);
    return formatted || '-';
};

// 載入付款方式列表
const loadPaymentMethods = async () => {
    try {
        loading.value = true;
        const params = {
            page: pagination.current,
            per_page: pagination.pageSize
        };

        if (filters.keyword) {
            params.keyword = filters.keyword;
        }

        if (filters.status !== undefined && filters.status !== null) {
            params.status = filters.status;
        }

        const response = await getPaymentMethods(params);

        if (response.result || response.success) {
            const data = response.data?.data || response.data || [];
            paymentMethods.value = data;
            const paginationData =
                response.data?.pagination || response.data || {};
            pagination.total = paginationData.total || 0;
            pagination.current =
                paginationData.current_page || pagination.current;
        } else {
            showErrorMessage(response.message || '獲取付款方式列表失敗');
        }
    } catch (error) {
        console.error('載入付款方式列表失敗:', error);
        showErrorMessage('載入付款方式列表失敗');
    } finally {
        loading.value = false;
    }
};

// 表格變更處理
const handleTableChange = ({ pagination: pag } = {}) => {
    if (!pag?.current) return;
    pagination.current = pag.current;
    pagination.pageSize = pag.pageSize;
    loadPaymentMethods();
};

// 搜尋處理
const handleSearch = () => {
    pagination.current = 1;
    loadPaymentMethods();
};

// 顯示新增 Modal
const showCreateModal = () => {
    isEdit.value = false;
    modalVisible.value = true;
    originalData.value = null;
    resetForm();
};

// 顯示編輯 Modal
const showEditModal = async (record) => {
    try {
        isEdit.value = true;
        modalVisible.value = true;
        originalData.value = { ...record };

        // 如果需要，可以重新獲取詳情
        const response = await getPaymentMethodDetail(record.id);
        if (response.result || response.success) {
            const detail = response.data || record;
            Object.assign(formData, {
                id: detail.id,
                code: detail.code,
                name: detail.name,
                status: detail.status ?? 1,
                mpg_channels: applyMpgChannels(detail)
            });
            originalData.value = {
                ...detail,
                mpg_channels: applyMpgChannels(detail)
            };
        } else {
            Object.assign(formData, {
                id: record.id,
                code: record.code,
                name: record.name,
                status: record.status ?? 1,
                mpg_channels: applyMpgChannels(record)
            });
            originalData.value = {
                ...record,
                mpg_channels: applyMpgChannels(record)
            };
        }
    } catch (error) {
        console.error('載入付款方式詳情失敗:', error);
        showErrorMessage('載入付款方式詳情失敗');
        // 使用現有資料
        Object.assign(formData, {
            id: record.id,
            code: record.code,
            name: record.name,
            status: record.status ?? 1,
            mpg_channels: applyMpgChannels(record)
        });
        originalData.value = {
            ...record,
            mpg_channels: applyMpgChannels(record)
        };
    }
};

// 重置表單
const resetForm = () => {
    originalData.value = null;
    Object.assign(formData, {
        id: null,
        code: '',
        name: '',
        status: 1,
        mpg_channels: defaultMpgChannels()
    });
    if (formRef.value) {
        formRef.value.resetFields();
    }
};

// 提交表單
const handleSubmit = async () => {
    try {
        await formRef.value.validate();
        submitting.value = true;

        let response;
        if (isEdit.value) {
            // 只送出有變更的欄位
            const changedFields = {};
            if (formData.name !== originalData.value.name) {
                changedFields.name = formData.name;
            }
            if (formData.status !== originalData.value.status) {
                changedFields.status = formData.status;
            }

            if (isNewebpayForm.value) {
                const prev = originalData.value.mpg_channels || {};
                const next = formData.mpg_channels || {};
                const mpgChanged = mpgChannelOptions.some(
                    (ch) => Boolean(prev[ch.key]) !== Boolean(next[ch.key])
                );
                if (mpgChanged) {
                    changedFields.settings = {
                        mpg_channels: { ...next }
                    };
                }
            }

            // 如果沒有變更，不發送請求
            if (Object.keys(changedFields).length === 0) {
                message.info('沒有變更');
                modalVisible.value = false;
                return;
            }

            response = await updatePaymentMethod(formData.id, changedFields);
        } else {
            const payload = {
                code: formData.code,
                name: formData.name,
                status: formData.status
            };
            if (isNewebpayForm.value) {
                payload.settings = {
                    mpg_channels: { ...formData.mpg_channels }
                };
            }
            response = await createPaymentMethod(payload);
        }

        if (response.success || response.result) {
            message.success(
                isEdit.value ? '付款方式更新成功' : '付款方式創建成功'
            );
            modalVisible.value = false;
            loadPaymentMethods();
        } else {
            showErrorMessage(
                response.message ||
                    `付款方式${isEdit.value ? '更新' : '創建'}失敗`
            );
        }
    } catch (error) {
        console.error('提交表單失敗:', error);
        if (error.errorFields) {
            showErrorMessage('請檢查表單輸入');
        } else {
            showErrorMessage(`付款方式${isEdit.value ? '更新' : '創建'}失敗`);
        }
    } finally {
        submitting.value = false;
    }
};

// 開啟刪除 Modal
const openDeleteModal = (record) => {
    deleteTarget.value = record;
    deleteModalVisible.value = true;
};

// 確認刪除
const confirmDelete = async () => {
    if (!deleteTarget.value) return;

    try {
        deleteLoading.value = true;
        const response = await deletePaymentMethod(deleteTarget.value.id);
        if (response.success || response.result) {
            message.success('付款方式刪除成功');
            deleteModalVisible.value = false;
            deleteTarget.value = null;
            loadPaymentMethods();
        } else {
            showErrorMessage(response.message || '付款方式刪除失敗');
        }
    } catch (error) {
        console.error('刪除付款方式失敗:', error);
        showErrorMessage('付款方式刪除失敗');
    } finally {
        deleteLoading.value = false;
    }
};

// 組件掛載時載入數據
onMounted(() => {
    if (canView.value) {
        loadPaymentMethods();
    }
});
</script>

<style scoped>
.page {
    padding: 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    min-height: 100vh;
}

.page-header {
    margin-bottom: 24px;
}

.page-header h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 8px 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    text-shadow: none;
    letter-spacing: normal;
}

.page-header p {
    margin: 0;
    color: #6a6a7a;
    font-size: 14px;
    font-family: 'Courier New', monospace;
}

.content {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 2px solid transparent;
    border-radius: 16px;
    padding: 32px;
    min-height: 400px;
    position: relative;
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.3);
}

.content::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
}

/* 確保查詢和新增按鈕尺寸一致 */
.toolbar-actions :deep(.unified-search-btn),
.toolbar-actions :deep(.unified-add-btn) {
    height: 32px !important;
    padding: 0 16px !important;
    font-size: 13px !important;
    line-height: 30px !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
}

/* 確保查詢按鈕顏色正確 */
.toolbar-actions :deep(.unified-search-btn.ant-btn) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid #7a9ba5 !important;
    color: #7a9ba5 !important;
}

.toolbar-actions :deep(.unified-search-btn.ant-btn:hover) {
    background: rgba(122, 155, 165, 0.1) !important;
    border-color: #7a9ba5 !important;
    color: #7a9ba5 !important;
    box-shadow: 0 2px 8px rgba(122, 155, 165, 0.3) !important;
}

.toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    position: relative;
    z-index: 1;
}

.filters {
    flex: 1;
}

.toolbar-actions {
    margin-left: auto;
}

/* 搜尋框樣式見 @/styles/page-layout.css（search-input-field） */

/* 下拉選單樣式 */
.status-select :deep(.ant-select-selector) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    height: 32px !important;
    transition: all 0.3s ease !important;
}

.status-select :deep(.ant-select-selector:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2) !important;
}

.status-select :deep(.ant-select-focused .ant-select-selector) {
    border-color: rgba(42, 74, 122, 0.8) !important;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.2) !important;
}

.status-select :deep(.ant-select-selection-item),
.status-select :deep(.ant-select-selection-placeholder) {
    line-height: 30px !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

.payment-methods-table {
    position: relative;
    z-index: 1;
}

/* DataTable component integration */
:deep(.ant-card) {
    background: transparent !important;
    border: none !important;
}

:deep(.ant-card-body) {
    padding: 0 !important;
}

.payment-methods-table :deep(.ant-table) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(42, 74, 122, 0.3) !important;
    border-radius: 12px !important;
    overflow: hidden !important;
}

.payment-methods-table :deep(.ant-table-thead > tr > th) {
    background: rgba(255, 255, 255, 0.9) !important;
    color: #64748b !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    border-bottom: 1px solid rgba(42, 74, 122, 0.3) !important;
}

.payment-methods-table :deep(.ant-table-tbody > tr > td) {
    background: rgba(255, 255, 255, 0.6) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.payment-methods-table :deep(.ant-table-tbody > tr:hover > td) {
    background: rgba(255, 255, 255, 0.6) !important;
    color: #2a2a3a !important;
}

.code-text {
    font-family: 'Courier New', monospace;
    font-weight: 500;
    color: #3b6ea5;
}

/* Dark theme modal styles */
:deep(.dark-modal .ant-modal-content) {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 2px solid transparent;
    border-radius: 16px;
    position: relative;
}

:deep(.dark-modal .ant-modal-content::before) {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
    z-index: -1;
}

:deep(.dark-modal .ant-modal-header) {
    background: transparent;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

:deep(.dark-modal .ant-modal-title) {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

:deep(.dark-modal .ant-modal-body) {
    background: transparent;
    color: #2a2a3a;
}

:deep(.dark-modal .ant-modal-footer) {
    background: transparent;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

/* 操作按鈕樣式 - 統一使用 DataTable 的樣式 */

/* 其他按鈕樣式 - 排除統一樣式按鈕 */
:deep(
    .ant-btn:not(.unified-search-btn):not(.unified-reset-btn):not(
            .unified-add-btn
        ):not(.action-btn-edit):not(.action-btn-delete):not(.action-btn-view)
) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 8px !important;
}

:deep(
    .ant-btn:not(.unified-search-btn):not(.unified-reset-btn):not(
            .unified-add-btn
        ):not(.action-btn-edit):not(.action-btn-delete):not(
            .action-btn-view
        ):hover
) {
    background: rgba(42, 74, 122, 0.1) !important;
    border-color: rgba(42, 74, 122, 0.6) !important;
    color: #2a2a3a !important;
}

:deep(.ant-btn-primary:not(.unified-add-btn)) {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5) !important;
    border: none !important;
    color: #ffffff !important;
    font-weight: 500 !important;
}

:deep(.ant-btn-primary:not(.unified-add-btn):hover) {
    background: linear-gradient(45deg, #3b6ea5, #2a4a7a) !important;
    box-shadow: 0 0 10px rgba(42, 74, 122, 0.4) !important;
}

:deep(.dark-modal .ant-btn-default) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
}

:deep(.dark-modal .ant-btn-default:hover) {
    background: rgba(168, 255, 246, 0.1) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
}

/* Form styles */
.payment-method-form :deep(.ant-form-item-label > label) {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.payment-method-form :deep(.ant-input) {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    color: #2a2a3a !important;
    padding: 8px 12px !important;
}

.payment-method-form :deep(.ant-input:hover) {
    border-color: #3b6ea5 !important;
}

.payment-method-form :deep(.ant-input:focus) {
    background: rgba(255, 255, 255, 1) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.2) !important;
}

.payment-method-form :deep(.ant-input:disabled) {
    background: rgba(255, 255, 255, 0.5) !important;
    color: rgba(42, 42, 58, 0.7) !important;
    cursor: not-allowed !important;
}

.payment-method-form :deep(.ant-radio-wrapper) {
    color: #2a2a3a;
}

.payment-method-form :deep(.ant-radio-checked .ant-radio-inner) {
    background-color: #0e7490;
    border-color: rgba(200, 200, 210, 0.4);
}

.payment-method-form :deep(.ant-radio:hover .ant-radio-inner) {
    border-color: rgba(200, 200, 210, 0.4);
}

.form-item-hint {
    font-size: 12px;
    color: #6a6a7a;
    margin-top: 4px;
    font-family: 'Courier New', monospace;
}

.mpg-divider {
    margin: 8px 0 16px;
    font-family: 'Courier New', monospace;
    color: #2a2a3a;
}

.mpg-alert {
    margin-bottom: 16px;
}

.mpg-grid {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 8px;
}

.mpg-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    border: 1px solid rgba(200, 200, 210, 0.45);
    background: rgba(255, 255, 255, 0.55);
}

.mpg-row__text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.mpg-row__label {
    font-size: 13px;
    font-weight: 700;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.mpg-row__hint {
    font-size: 11px;
    color: #6a6a7a;
}

/* 刪除 Modal 樣式 */
.delete-modal-content {
    font-family: 'Courier New', monospace;
}

.delete-payment-info {
    margin-bottom: 24px;
}

.delete-section-title {
    font-size: 16px;
    font-weight: 700;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 2px solid rgba(138, 175, 255, 0.3);
}

.payment-code-display {
    font-size: 14px;
    font-weight: 600;
    color: #3b6ea5;
    font-family: 'Courier New', monospace;
}

.payment-name-display {
    font-size: 15px;
    font-weight: 600;
    color: #2a2a3a;
}

.delete-action-hint {
    margin-top: 20px;
}

.delete-modal-content :deep(.ant-descriptions) {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(138, 175, 255, 0.2);
}

.delete-modal-content :deep(.ant-descriptions-item-label) {
    background: rgba(138, 175, 255, 0.1) !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    border-right: 1px solid rgba(138, 175, 255, 0.15) !important;
}

.delete-modal-content :deep(.ant-descriptions-item-content) {
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    background: rgba(255, 255, 255, 0.6) !important;
}

/* Select 樣式 */
:deep(.vaporwave-select .ant-select-selector) {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
}

:deep(.vaporwave-select .ant-select-selector:hover) {
    border-color: rgba(200, 200, 210, 0.6) !important;
}

:deep(.vaporwave-select.ant-select-focused .ant-select-selector) {
    border-color: rgba(200, 200, 210, 0.4) !important;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.2) !important;
}
</style>
